import { KsefEnumValueDto, KsefOutgoingEnumsDto } from '../Types/ksefOutgoing.types';

const toCamelCase = (value: string): string => {
  if (!value) {
    return value;
  }

  return `${value.charAt(0).toLowerCase()}${value.slice(1)}`;
};

const setEnumNameMapKey = (
  accumulator: Record<string, number>,
  key: string,
  item: KsefEnumValueDto
): void => {
  const existingId = accumulator[key];
  if (existingId !== undefined && existingId !== item.id) {
    console.warn(
      `[KSeF][buildEnumNameMap] Key collision detected for normalized key "${key}". ` +
        `Previous id: ${existingId}, next id: ${item.id}, enum name: "${item.name}".`
    );
  }

  accumulator[key] = item.id;
};

const buildEnumNameMap = (values?: KsefEnumValueDto[]): Record<string, number> => {
  if (!values?.length) {
    return {};
  }

  return values.reduce<Record<string, number>>((accumulator, item) => {
    const raw = item.name;
    const camel = toCamelCase(item.name);

    setEnumNameMapKey(accumulator, raw, item);
    setEnumNameMapKey(accumulator, camel, item);
    setEnumNameMapKey(accumulator, raw.toLowerCase(), item);
    setEnumNameMapKey(accumulator, camel.toLowerCase(), item);

    return accumulator;
  }, {});
};

const resolveLabelByName = (
  value: string | null | undefined,
  labels: Record<number, string> | undefined,
  nameMap: Record<string, number>
): string | null | undefined => {
  if (!value) {
    return value;
  }

  const id = nameMap[value] ?? nameMap[value.toLowerCase()];
  if (id === undefined || !labels) {
    return value;
  }

  return labels[id] ?? value;
};

export interface KsefOutgoingLabelResolvers {
  stateLabel: (state?: string | null) => string | null | undefined;
  decisionLabel: (decision?: string | null) => string | null | undefined;
  reasonCodeLabel: (reasonCode?: string | null) => string | null | undefined;
  reasonCodesLabel: (reasonCodes?: string[]) => string[];
}

export const buildKsefOutgoingLabelResolvers = (
  enums?: KsefOutgoingEnumsDto
): KsefOutgoingLabelResolvers => {
  const stateNameMap = buildEnumNameMap(enums?.states);
  const decisionNameMap = buildEnumNameMap(enums?.eligibilityDecisions);
  const reasonCodeNameMap = buildEnumNameMap(enums?.eligibilityReasonCodes);

  return {
    stateLabel: (state) => resolveLabelByName(state, enums?.stateLabels, stateNameMap),
    decisionLabel: (decision) =>
      resolveLabelByName(decision, enums?.eligibilityDecisionLabels, decisionNameMap),
    reasonCodeLabel: (reasonCode) =>
      resolveLabelByName(reasonCode, enums?.eligibilityReasonCodeLabels, reasonCodeNameMap),
    reasonCodesLabel: (reasonCodes) =>
      reasonCodes?.map(
        (reasonCode) =>
          resolveLabelByName(reasonCode, enums?.eligibilityReasonCodeLabels, reasonCodeNameMap) ??
          reasonCode
      ) ?? [],
  };
};
